import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

const LOGO_MARK = "https://customer-assets.emergentagent.com/job_5c120cb0-6c39-4574-8992-e03c5cf62daa/artifacts/9e7hexs6_logo-ed-removebg-preview.png";

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState('');
  
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (isRegister) {
        await register(name, email, password);
        toast.success('Compte créé avec succès !');
      } else {
        await login(email, password);
        toast.success('Connexion réussie !');
      }
      navigate('/admin');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur de connexion';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-12">
          <Link to="/">
            <img 
              src={LOGO_MARK} 
              alt="ED" 
              className="h-20 w-auto mx-auto mb-6"
            />
          </Link>
          <h1 className="font-serif text-3xl font-medium tracking-tight">
            {isRegister ? 'Créer un compte' : 'Administration'}
          </h1>
          <p className="text-muted-foreground mt-2">
            {isRegister ? 'Créez votre compte administrateur' : 'Connectez-vous pour gérer votre portfolio'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {isRegister && (
            <div>
              <label className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2 block">
                Nom
              </label>
              <Input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Votre nom"
                data-testid="login-name"
              />
            </div>
          )}
          
          <div>
            <label className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2 block">
              Email
            </label>
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              data-testid="login-email"
            />
          </div>

          <div>
            <label className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2 block">
              Mot de passe
            </label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <Button 
            type="submit" 
            disabled={loading}
            className="w-full rounded-full py-6"
            data-testid="login-submit"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              isRegister ? 'Créer le compte' : 'Se connecter'
            )}
          </Button>
        </form>

        {/* Toggle Register/Login */}
        <div className="mt-8 text-center">
          <button
            onClick={() => setIsRegister(!isRegister)}
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
            data-testid="toggle-auth-mode"
          >
            {isRegister ? 'Déjà un compte ? Se connecter' : 'Pas encore de compte ? Créer un compte'}
          </button>
        </div>

        {/* Back to site */}
        <div className="mt-4 text-center">
          <Link 
            to="/" 
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
            data-testid="back-to-site"
          >
            ← Retour au site
          </Link>
        </div>
      </div>
    </div>
  );
}
